// src/components/exam-player/renderers/WritingPromptRenderer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export default function WritingPromptRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const body = item?.body || {};

  // stem = صورت سؤال
  const stemHtml =
    item?.stem?.fa || item?.stem?.en || t("preview.no_stem", "بدون متن");

  // متن reading اختیاری
  const readingHtml = body?.text?.fa || body?.text?.en || body?.text || ""; // برای سازگاری اگر text رشته ساده بود

  const audioUrl = body?.audio_url || "";

  // مقدار اولیه پاسخ
  const initialText = useMemo(() => {
    if (!initialAnswers) return "";
    if (typeof initialAnswers === "string") return initialAnswers;
    if (
      typeof initialAnswers === "object" &&
      typeof initialAnswers.text === "string"
    ) {
      return initialAnswers.text;
    }
    return "";
  }, [initialAnswers]);

  const [text, setText] = useState(initialText);

  useEffect(() => {
    setText(initialText);
  }, [initialText]);

  const countWords = (value) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).filter(Boolean).length;
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setText(v);

    const payload = {
      text: v,
      wordCount: countWords(v),
    };

    onAnswerChange?.(payload);
  };

  const minWords =
    typeof body.minWords === "number" && !Number.isNaN(body.minWords)
      ? body.minWords
      : 0;
  const maxWords =
    typeof body.maxWords === "number" && !Number.isNaN(body.maxWords)
      ? body.maxWords
      : 0;

  return (
    <div dir={isRtl ? "rtl" : "ltr"}>
      {/* صورت سؤال */}
      <div className="prose prose-sm max-w-none mb-4">
        <div dangerouslySetInnerHTML={{ __html: stemHtml }} />
        {typeof questionNumber === "number" && (
          <div className="text-xs text-gray-500 mt-1">
            {t("preview.question_number", {
              n: questionNumber,
            })}
          </div>
        )}
      </div>

      {/* متن Reading (اختیاری) */}
      {readingHtml && (
        <div className="prose prose-sm max-w-none mb-4 border rounded-lg p-3 bg-gray-50">
          <div dangerouslySetInnerHTML={{ __html: readingHtml }} />
        </div>
      )}

      {/* فایل صوتی (اختیاری) */}
      {audioUrl && (
        <div className="mb-4">
          <audio controls src={audioUrl} className="w-full">
            {t(
              "writing_prompt.audio_not_supported",
              "مرورگر شما از پخش صوت پشتیبانی نمی‌کند."
            )}
          </audio>
        </div>
      )}

      {/* باکس پاسخ نوشتاری */}
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium">
          {t("writing_prompt.your_answer", "پاسخ شما")}
        </span>
        <span className="text-xs text-gray-500">
          {t("writing_prompt.word_count", {
            count: countWords(text),
          })}{" "}
          {minWords || maxWords
            ? `(${minWords ? `min ${minWords}` : ""}${
                minWords && maxWords ? " / " : ""
              }${maxWords ? `max ${maxWords}` : ""})`
            : ""}
        </span>
      </div>

      <textarea
        className="w-full border rounded-lg px-3 py-2 text-sm min-h-[160px] focus:outline-none focus:ring-2 focus:ring-purple-500"
        value={text}
        onChange={handleChange}
        placeholder={t(
          "writing_prompt.answer_placeholder",
          "پاسخ خود را اینجا بنویسید…"
        )}
      />
    </div>
  );
}
